import {
    NyuDataPointsWithParsedTime,
    NyuSeriesProperties,
} from '../../../../../shared/types'

/**
 * Determines if data is high frequency (monthly/quarterly/semiannual) based on time gaps
 */
function isHighFrequencyData(dataPoints: { time: number }[]): boolean {
    if (dataPoints.length <= 1) return false

    const timeGaps = dataPoints
        .slice(1)
        .map((point, i) => point.time - dataPoints[i].time)
    const avgGap = timeGaps.reduce((sum, gap) => sum + gap, 0) / timeGaps.length
    return avgGap < 0.8
}

/**
 * Gets valid data points for a series, sorted by time
 */
function getValidDataPoints(
    dataPoints: NyuDataPointsWithParsedTime,
    lineDataKey: string
): Array<{ time: number; [key: string]: any }> {
    return dataPoints
        .filter((point) => {
            const pointValue = point[lineDataKey]
            return pointValue !== null && pointValue !== undefined
        })
        .sort((a, b) => a.time - b.time)
}

/**
 * Gets strategically placed data points for shape display
 */
function getShapeDisplayPoints(
    dataPoints: Array<{ time: number }>
): Set<number> {
    if (dataPoints.length === 0) return new Set()

    const sortedPoints = dataPoints.sort((a, b) => a.time - b.time)
    const timeSpan =
        sortedPoints[sortedPoints.length - 1].time - sortedPoints[0].time

    if (timeSpan < 3) {
        // For short time spans, use strategic sampling
        const targetShapeCount = Math.max(
            3,
            Math.min(8, Math.floor(dataPoints.length / 4))
        )
        const step = Math.max(
            1,
            Math.floor(dataPoints.length / targetShapeCount)
        )

        const selectedTimes = new Set<number>()
        for (let i = 0; i < dataPoints.length; i += step) {
            selectedTimes.add(sortedPoints[i].time)
        }

        // Always include the last point
        if (dataPoints.length > 1) {
            selectedTimes.add(sortedPoints[sortedPoints.length - 1].time)
        }

        return selectedTimes
    }

    return getFirstPointPerYear(sortedPoints)
}

/**
 * Gets the first data point for each year from high frequency data
 */
function getFirstPointPerYear(
    dataPoints: Array<{ time: number }>
): Set<number> {
    const yearGroups = new Map<number, { time: number }>()

    dataPoints.forEach((point) => {
        const year = Math.floor(point.time)
        if (!yearGroups.has(year) || point.time < yearGroups.get(year)!.time) {
            yearGroups.set(year, point)
        }
    })

    return new Set(Array.from(yearGroups.values()).map((point) => point.time))
}

/**
 * Calculates which data points should show shapes for each series
 */
export function calculateShapeVisibility(
    seriesProperties: NyuSeriesProperties,
    dataPoints: NyuDataPointsWithParsedTime,
    isHighContrastEnabled: boolean
): Map<string, Set<number>> {
    const map = new Map<string, Set<number>>()

    if (!isHighContrastEnabled) {
        return map
    }

    seriesProperties.forEach((series, shapeIndex) => {
        const lineDataKey = series.key
        const validPoints = getValidDataPoints(dataPoints, lineDataKey)

        if (validPoints.length === 0) return

        const isHighFrequency = isHighFrequencyData(validPoints)
        const visibleTimes = isHighFrequency
            ? getShapeDisplayPoints(validPoints)
            : new Set(validPoints.map((point) => point.time))

        map.set(`${shapeIndex}-${lineDataKey}`, visibleTimes)
    })

    return map
}
